/**
 * @fileoverview Memory Game logic hook
 * @description Manages card flipping, matching, timer, and scoring
 */

import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import {
  MemoryCard,
  MemoryGameState,
  GridSize,
  ImageSetType,
  GameStatus,
  MemoryGameResult,
  DIFFICULTY_CONFIGS,
  RANDOM_IMAGES,
} from "../types";

interface UseMemoryGameOptions {
  sessionId: string;
  gridSize?: GridSize;
  imageSetType?: ImageSetType;
  familyImages?: string[];
  onGameEnd?: (result: MemoryGameResult) => void;
}

interface UseMemoryGameReturn {
  cards: MemoryCard[];
  revealedCards: string[];
  gameStatus: GameStatus;
  moves: number;
  matchedPairs: number;
  totalPairs: number;
  elapsedTime: number;
  isLoading: boolean;
  error: string | null;
  disabled: boolean;
  gridConfig: typeof DIFFICULTY_CONFIGS["4x4"];
  flipCard: (cardId: string) => void;
  startGame: () => void;
  resetGame: () => Promise<void>;
  pauseGame: () => void;
  resumeGame: () => void;
}

/**
 * Shuffle an array using Fisher-Yates algorithm
 */
const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * Generate card pairs for the game
 */
const generateCards = (
  gridSize: GridSize,
  imageSetType: ImageSetType,
  familyImages: string[] = []
): MemoryCard[] => {
  const config = DIFFICULTY_CONFIGS[gridSize];
  const pairsNeeded = config.pairs;

  // Get images based on type
  let images: string[];
  if (imageSetType === "family" && familyImages.length >= pairsNeeded) {
    images = shuffleArray(familyImages).slice(0, pairsNeeded);
  } else {
    // Use random images
    const randomPool = shuffleArray(RANDOM_IMAGES);
    images = randomPool.slice(0, Math.min(pairsNeeded, randomPool.length));
    
    // If we need more images than available, repeat some
    while (images.length < pairsNeeded) {
      images.push(randomPool[images.length % randomPool.length]);
    }
  }

  // Create pairs
  const cardPairs: MemoryCard[] = [];
  images.forEach((imageUrl, index) => {
    const pairId = `pair-${index}`;
    
    // Card 1
    cardPairs.push({
      id: `card-${index}-a`,
      pairId,
      imageUrl,
      state: "hidden",
      position: cardPairs.length,
    });
    
    // Card 2 (matching pair)
    cardPairs.push({
      id: `card-${index}-b`,
      pairId,
      imageUrl,
      state: "hidden",
      position: cardPairs.length,
    });
  });

  // Shuffle and update positions
  const shuffledCards = shuffleArray(cardPairs);
  return shuffledCards.map((card, index) => ({
    ...card,
    position: index,
  }));
};

/**
 * Get image URL for a card
 */
export const getCardImageUrl = (
  imageUrl: string,
  imageSetType: ImageSetType
): string => {
  if (imageSetType === "family") {
    // Family images are stored in Supabase storage
    return imageUrl;
  }
  // Random images are local assets
  return `memory_game_images/${imageUrl}`;
};

/**
 * Main Memory Game hook
 */
export const useMemoryGame = ({
  sessionId,
  gridSize = "4x4",
  imageSetType = "random",
  familyImages = [],
  onGameEnd,
}: UseMemoryGameOptions): UseMemoryGameReturn => {
  const { user } = useAuth();

  const [cards, setCards] = useState<MemoryCard[]>([]);
  const [revealedCards, setRevealedCards] = useState<string[]>([]);
  const [gameStatus, setGameStatus] = useState<GameStatus>("idle");
  const [moves, setMoves] = useState(0);
  const [matchedPairs, setMatchedPairs] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const matchCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const gridConfig = useMemo(
    () => DIFFICULTY_CONFIGS[gridSize],
    [gridSize]
  );

  const totalPairs = gridConfig.pairs;

  const disabled = useMemo(
    () =>
      gameStatus !== "playing" ||
      isProcessing ||
      revealedCards.length >= 2,
    [gameStatus, isProcessing, revealedCards.length]
  );

  /**
   * Load or initialize game state
   */
  const loadGameState = useCallback(async () => {
    if (!sessionId || !user) return;

    try {
      setIsLoading(true);
      setError(null);

      const { data: session, error: sessionError } = await supabase
        .from("game_sessions")
        .select("game_state, status")
        .eq("id", sessionId)
        .single();

      if (sessionError) throw sessionError;

      const state = session.game_state as MemoryGameState | null;

      if (state && state.cards?.length > 0) {
        // Resume existing game
        setCards(state.cards);
        setRevealedCards(state.revealedCards || []);
        setGameStatus(state.status);
        setMoves(state.moves);
        setMatchedPairs(state.matchedPairs);

        if (state.startedAt) {
          const elapsed = Math.floor(
            (Date.now() - new Date(state.startedAt).getTime()) / 1000
          );
          setElapsedTime(elapsed);
          setStartTime(new Date(state.startedAt).getTime());
        }
      } else {
        // Initialize new game
        const newCards = generateCards(gridSize, imageSetType, familyImages);
        setCards(newCards);
        setGameStatus("idle");
      }
    } catch (err) {
      console.error("Error loading game state:", err);
      setError("Kon spel niet laden");
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, user, gridSize, imageSetType, familyImages]);

  /**
   * Start the timer
   */
  useEffect(() => {
    if (gameStatus === "playing" && !timerRef.current) {
      timerRef.current = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
    } else if (gameStatus !== "playing" && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [gameStatus]);

  /**
   * Load game on mount
   */
  useEffect(() => {
    loadGameState();
  }, [loadGameState]);

  /**
   * Cleanup timeouts on unmount
   */
  useEffect(() => {
    return () => {
      if (matchCheckTimeoutRef.current) {
        clearTimeout(matchCheckTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Start the game
   */
  const startGame = useCallback(() => {
    if (cards.length === 0) {
      const newCards = generateCards(gridSize, imageSetType, familyImages);
      setCards(newCards);
    }
    
    setGameStatus("playing");
    setStartTime(Date.now());
    setElapsedTime(0);
    setMoves(0);
    setMatchedPairs(0);
    setRevealedCards([]);

    // Save initial state
    const newState: MemoryGameState = {
      cards: cards.length > 0 ? cards : generateCards(gridSize, imageSetType, familyImages),
      gridSize,
      imageSetType,
      status: "playing",
      moves: 0,
      matchedPairs: 0,
      totalPairs,
      startedAt: new Date().toISOString(),
      completedAt: null,
      revealedCards: [],
      lastMoveAt: null,
    };

    supabase
      .from("game_sessions")
      .update({
        game_state: newState,
        status: "active",
      })
      .eq("id", sessionId)
      .then();
  }, [cards, gridSize, imageSetType, familyImages, totalPairs, sessionId]);

  /**
   * Handle card flip
   */
  const flipCard = useCallback(
    (cardId: string) => {
      if (disabled) return;

      const card = cards.find((c) => c.id === cardId);
      if (!card || card.state === "matched" || revealedCards.includes(cardId)) {
        return;
      }

      const newRevealed = [...revealedCards, cardId];
      setRevealedCards(newRevealed);

      // If this is the second card
      if (newRevealed.length === 2) {
        setMoves((prev) => prev + 1);
        setIsProcessing(true);

        const [firstId, secondId] = newRevealed;
        const firstCard = cards.find((c) => c.id === firstId);
        const secondCard = cards.find((c) => c.id === secondId);

        if (firstCard && secondCard && firstCard.pairId === secondCard.pairId) {
          // Match found!
          matchCheckTimeoutRef.current = setTimeout(() => {
            setCards((prevCards) =>
              prevCards.map((c) =>
                c.pairId === firstCard.pairId
                  ? { ...c, state: "matched" as const }
                  : c
              )
            );
            setMatchedPairs((prev) => {
              const newMatched = prev + 1;
              
              // Check for game completion
              if (newMatched === totalPairs) {
                handleGameComplete(moves + 1, elapsedTime);
              }
              
              return newMatched;
            });
            setRevealedCards([]);
            setIsProcessing(false);
          }, 500);
        } else {
          // No match - hide cards after delay
          matchCheckTimeoutRef.current = setTimeout(() => {
            setRevealedCards([]);
            setIsProcessing(false);
          }, 1000);
        }
      }
    },
    [cards, revealedCards, disabled, moves, elapsedTime, totalPairs]
  );

  /**
   * Handle game completion
   */
  const handleGameComplete = useCallback(
    async (finalMoves: number, finalTime: number) => {
      setGameStatus("completed");

      const isPerfect = finalMoves === totalPairs;
      const config = DIFFICULTY_CONFIGS[gridSize];
      
      // Calculate score
      // Base score for completion + time bonus + perfect bonus
      const baseScore = totalPairs * 10;
      const timeBonus = Math.max(
        0,
        Math.floor((config.timeBonus - finalTime) / 2)
      );
      const perfectBonus = isPerfect ? 50 : 0;
      const score = baseScore + timeBonus + perfectBonus;

      const result: MemoryGameResult = {
        completed: true,
        moves: finalMoves,
        timeSeconds: finalTime,
        pairs: totalPairs,
        isPerfect,
        score,
      };

      // Update session - preserve existing player_times for versus mode
      try {
        // First fetch current session to preserve player_times
        const { data: currentSession } = await supabase
          .from("game_sessions")
          .select("game_state, session_type")
          .eq("id", sessionId)
          .single();
        
        const existingState = currentSession?.game_state as Record<string, unknown> || {};
        const existingPlayerTimes = existingState.player_times as Record<string, number> | undefined;
        const isVersusMode = currentSession?.session_type === "versus";
        
        // For versus mode, don't set winner_user_id or status here - let handleGameEnd handle it
        const updateData: Record<string, unknown> = {
          game_state: {
            cards,
            gridSize,
            imageSetType,
            status: "completed",
            moves: finalMoves,
            matchedPairs: totalPairs,
            totalPairs,
            startedAt: startTime ? new Date(startTime).toISOString() : null,
            completedAt: new Date().toISOString(),
            revealedCards: [],
            lastMoveAt: new Date().toISOString(),
            // Preserve player_times if they exist
            ...(existingPlayerTimes && { player_times: existingPlayerTimes }),
          },
        };
        
        // Only update status/winner for solo mode - versus mode is handled by handleGameEnd
        if (!isVersusMode) {
          updateData.status = "completed";
          updateData.completed_at = new Date().toISOString();
          updateData.winner_user_id = user?.id;
        }
        
        await supabase
          .from("game_sessions")
          .update(updateData)
          .eq("id", sessionId);
      } catch (err) {
        console.error("Error saving game completion:", err);
      }

      if (onGameEnd) {
        onGameEnd(result);
      }
    },
    [
      totalPairs,
      gridSize,
      user,
      cards,
      imageSetType,
      startTime,
      sessionId,
      onGameEnd,
    ]
  );

  /**
   * Reset the game
   */
  const resetGame = useCallback(async () => {
    const newCards = generateCards(gridSize, imageSetType, familyImages);
    setCards(newCards);
    setRevealedCards([]);
    setGameStatus("idle");
    setMoves(0);
    setMatchedPairs(0);
    setElapsedTime(0);
    setStartTime(null);
    setIsProcessing(false);

    try {
      await supabase
        .from("game_sessions")
        .update({
          status: "waiting",
          completed_at: null,
          winner_user_id: null,
          game_state: null,
        })
        .eq("id", sessionId);
    } catch (err) {
      console.error("Error resetting game:", err);
    }
  }, [gridSize, imageSetType, familyImages, sessionId]);

  /**
   * Pause the game
   */
  const pauseGame = useCallback(() => {
    if (gameStatus === "playing") {
      setGameStatus("paused");
    }
  }, [gameStatus]);

  /**
   * Resume the game
   */
  const resumeGame = useCallback(() => {
    if (gameStatus === "paused") {
      setGameStatus("playing");
    }
  }, [gameStatus]);

  return {
    cards,
    revealedCards,
    gameStatus,
    moves,
    matchedPairs,
    totalPairs,
    elapsedTime,
    isLoading,
    error,
    disabled,
    gridConfig,
    flipCard,
    startGame,
    resetGame,
    pauseGame,
    resumeGame,
  };
};

export default useMemoryGame;

